import { Dialog, Transition } from '@headlessui/react';
import { useFooterInView } from '@lib/hooks/use-footer-in-view';
import useToggleState from '@lib/hooks/use-toggle-state';
import { cn } from '@lib/util/cn';
import { getProductPrice } from '@lib/util/get-product-price';
import { isSimpleProduct } from '@lib/util/product';
import { Button } from '@modules/common/components/button';
import { ChevronDown, XClose } from '@modules/common/icons';
import StockIndicator from '@modules/products/components/stock-indicator';
import type { CTAState } from '@modules/products/utils/requires-consultation';
import type React from 'react';
import { Fragment, useMemo } from 'react';
import type {
  ProductActionsVariantVM,
  ProductActionsVM,
} from 'types/view-models';
import OptionSelect from './option-select';

type MobileActionsProps = {
  product: ProductActionsVM;
  variant?: ProductActionsVariantVM;
  options: Record<string, string | undefined>;
  updateOptions: (title: string, value: string) => void;
  inStock?: boolean;
  // Suppressed for variants blocked by metadata (not_for_sale / unclassified)
  // so the sticky bar never shows "In Stock" beside a hard-disabled CTA.
  showStockBadge?: boolean;
  stockBlocked?: boolean;
  // See `ProductActions`' `stockCheckPending`: true until the first stock
  // check for the selected variant resolves. Gates all three CTA variants
  // here the same way, since the full-page consultation button has no
  // click-time backstop of its own.
  stockCheckPending?: boolean;
  handleAddToCart: () => void;
  isAdding?: boolean;
  show: boolean;
  optionsDisabled: boolean;
  consultationUrl?: string;
  onStartConsultation?: () => void;
  onNavigateToConsultation?: (url: string) => void;
  isStartingConsultation?: boolean;
  ctaState: CTAState;
  onOpenConsultationModal?: () => void;
};

const MobileActions: React.FC<MobileActionsProps> = ({
  product,
  variant,
  options,
  updateOptions,
  inStock,
  showStockBadge = true,
  stockBlocked,
  stockCheckPending,
  handleAddToCart,
  isAdding,
  show,
  optionsDisabled,
  consultationUrl,
  onStartConsultation,
  onNavigateToConsultation,
  isStartingConsultation,
  ctaState,
  onOpenConsultationModal,
}) => {
  const { state, open, close } = useToggleState();
  const footerInView = useFooterInView();

  const price = getProductPrice({
    product,
    variantId: variant?.id,
  });

  const selectedPrice = useMemo(() => {
    if (!price) {
      return null;
    }
    const { variantPrice, cheapestPrice } = price;

    return variantPrice || cheapestPrice || null;
  }, [price]);

  const isSimple = isSimpleProduct(product);

  const handleUpdateOptions = (optionId: string, value: string) => {
    updateOptions(optionId, value);
    const updatedOptions = { ...options, [optionId]: value };
    const isNowComplete = (product.options ?? []).every(
      (opt) => updatedOptions[opt.id] !== undefined,
    );
    if (isNowComplete) {
      close();
    }
  };

  const buttonClassName = cn(
    'flex-1',
    isSimple && 'tablet:flex-none tablet:w-fit',
  );

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: CTA branches over OOS state and the five mutually exclusive ctaState kinds
  const renderMobileCTA = () => {
    // Checked before `!inStock`/`stockBlocked`: while pending, `inStock` is
    // still whatever the (possibly stale) static snapshot said, so checking
    // it first could render "Out of stock" for a variant that's actually
    // available — the live check just hasn't resolved yet.
    if (variant && stockCheckPending) {
      return (
        <Button
          disabled
          variant="primary"
          className={buttonClassName}
          data-testid="mobile-cart-button"
        >
          <span translate="no">Loading…</span>
        </Button>
      );
    }
    // OOS check is hoisted: regardless of consultation mode, an out-of-stock
    // variant must never reach the add-to-cart or consultation flow. Same
    // for stockBlocked — the basket already holds all that's currently
    // available for this variant.
    if (variant && !inStock) {
      return (
        <Button
          disabled
          variant="primary"
          className={buttonClassName}
          data-testid="mobile-cart-button"
        >
          <span translate="no">Out of stock</span>
        </Button>
      );
    }
    if (variant && stockBlocked) {
      return (
        <Button
          disabled
          variant="primary"
          className={buttonClassName}
          data-testid="mobile-cart-button"
        >
          <span translate="no">Max quantity in basket</span>
        </Button>
      );
    }
    if (ctaState.kind === 'mounting') {
      return (
        <Button
          disabled
          variant="primary"
          className={buttonClassName}
          data-testid="mobile-cart-button"
        >
          <span translate="no">Loading…</span>
        </Button>
      );
    }
    if (ctaState.kind === 'no-variant') {
      return (
        <Button
          disabled
          variant="primary"
          className={buttonClassName}
          data-testid="mobile-cart-button"
        >
          <span translate="no">Select option</span>
        </Button>
      );
    }
    if (ctaState.kind === 'unclassified') {
      return (
        <Button
          disabled
          variant="primary"
          className={buttonClassName}
          data-testid="mobile-cart-button"
        >
          <span translate="no">Currently unavailable</span>
        </Button>
      );
    }
    if (ctaState.kind === 'not-for-sale') {
      return (
        <Button
          disabled
          variant="primary"
          className={buttonClassName}
          data-testid="mobile-cart-button"
        >
          <span translate="no">Not available to purchase</span>
        </Button>
      );
    }
    if (ctaState.kind === 'consultation' && ctaState.mode === 'full_page') {
      if (!consultationUrl) {
        return (
          <Button
            disabled
            variant="primary"
            className={buttonClassName}
            data-testid="mobile-cart-button"
          >
            <span translate="no">Start consultation</span>
          </Button>
        );
      }
      return (
        <Button
          variant="primary"
          className={buttonClassName}
          isLoading={isStartingConsultation}
          data-testid="mobile-cart-button"
          onClick={() => onNavigateToConsultation?.(consultationUrl)}
        >
          <span translate="no">Start consultation</span>
        </Button>
      );
    }
    if (ctaState.kind === 'consultation' && ctaState.mode === 'modal') {
      return (
        <Button
          onClick={() => {
            onOpenConsultationModal?.();
            onStartConsultation?.();
          }}
          disabled={!variant}
          variant="primary"
          className={buttonClassName}
          isLoading={isAdding}
          data-testid="mobile-cart-button"
        >
          <span translate="no">Add to Basket</span>
        </Button>
      );
    }
    // ctaState.kind === 'gsl' | 'other'
    return (
      <Button
        onClick={handleAddToCart}
        disabled={!inStock || !variant}
        variant="primary"
        className={buttonClassName}
        isLoading={isAdding}
        data-testid="mobile-cart-button"
      >
        <span translate="no">Add to Basket</span>
      </Button>
    );
  };

  return (
    <>
      <div
        className={cn('lg:hidden inset-x-0 bottom-0 fixed z-[50]', {
          'pointer-events-none': !show || footerInView,
        })}
      >
        <Transition
          as={Fragment}
          show={show && !footerInView}
          enter="ease-in-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="bg-white flex flex-col gap-y-2 justify-center items-start p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] w-full border-t border-gray-200 shadow-[0_-2px_10px_rgba(0,0,0,0.08)]"
            data-testid="mobile-actions"
          >
            <div className="flex w-full items-center justify-between gap-2">
              <span
                data-testid="mobile-title"
                className="max-w-[242px] mobile:max-w-[272px] tablet:max-w-full font-heading line-clamp-1 font-light"
                translate="no"
              >
                {product.title}
              </span>
              {variant && showStockBadge ? (
                <StockIndicator inStock={inStock} />
              ) : null}
            </div>
            {selectedPrice && !isSimple ? (
              <Price selectedPrice={selectedPrice} />
            ) : null}
            <div
              className={cn('w-full', {
                'grid grid-cols-2 gap-x-4': !isSimple,
                'flex items-center tablet:justify-between gap-4': isSimple,
              })}
            >
              {!isSimple && (
                <Button
                  onClick={open}
                  variant="secondary"
                  className="w-full"
                  data-testid="mobile-actions-button"
                >
                  <div className="flex items-center justify-between w-full">
                    <span translate="no">
                      {variant
                        ? Object.values(options).join(' / ')
                        : 'Select Option'}
                    </span>
                    <ChevronDown aria-hidden="true" />
                  </div>
                </Button>
              )}
              {selectedPrice && isSimple ? (
                <Price selectedPrice={selectedPrice} />
              ) : null}
              {renderMobileCTA()}
            </div>
          </div>
        </Transition>
      </div>
      <Transition appear show={state} as={Fragment}>
        <Dialog as="div" className="relative z-[75]" onClose={close}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-700 bg-opacity-75 backdrop-blur-xs" />
          </Transition.Child>

          <div className="fixed bottom-0 inset-x-0">
            <div className="flex min-h-full h-full items-center justify-center text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Dialog.Panel
                  className="w-full h-full transform overflow-hidden text-left flex flex-col gap-y-3"
                  data-testid="mobile-actions-modal"
                >
                  <div className="w-full flex justify-end pr-6">
                    <button
                      type="button"
                      onClick={close}
                      className="bg-white w-12 h-12 cursor-pointer rounded-full text-ui-fg-base flex justify-center items-center"
                      data-testid="close-modal-button"
                    >
                      <XClose />
                    </button>
                  </div>
                  <div className="bg-white px-6 py-12">
                    {(product.variants?.length ?? 0) > 1 && (
                      <div className="flex flex-col gap-y-6">
                        {(product.options || []).map((option) => {
                          return (
                            <div key={option.id}>
                              <OptionSelect
                                option={option}
                                current={options[option.id]}
                                updateOption={handleUpdateOptions}
                                title={option.title ?? ''}
                                disabled={optionsDisabled}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

const Price = ({
  selectedPrice,
}: {
  selectedPrice: {
    calculated_price_number: number;
    calculated_price: string;
    original_price_number: number;
    original_price: string;
    currency_code: string;
    price_type: string;
    percentage_diff: string;
  };
}) => {
  return (
    <div className="flex items-end gap-x-2 w-max text-ui-fg-base">
      {selectedPrice.price_type === 'sale' && (
        <p>
          <span className="line-through nw-typography-paragraph-xs">
            {selectedPrice.original_price}
          </span>
        </p>
      )}
      <span
        className={cn('font-bold', {
          'text-fg-interactive-strong': selectedPrice.price_type === 'sale',
        })}
      >
        {selectedPrice.calculated_price}
      </span>
    </div>
  );
};

export default MobileActions;
